import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Heading, Link, Stack, Text, useToast } from '@chakra-ui/react';
import { useUserContext } from '@providers/useUserContext';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { clearPendingReferral, readPendingReferral, useReferralContext } from 'hooks/useReferralContext';
import { HTTP } from '@utils/http';
import Auth from './Auth';
import { ReferralWelcome } from './ReferralWelcome';
import UserProfile from './UserProfile';
import { getBrandColor } from 'utils/brandColor';

const AUTH_SCROLL_TARGET_ID = 'msc-auth-form';

export function Account() {
  const { isLoggedIn, logout } = useUserContext();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const referralContext = useReferralContext();

  const primaryColor = getBrandColor();
  const toast = useToast();
  const queryClient = useQueryClient();
  const claimStartedRef = useRef(false);

  // Parrainage d'un client qui avait déjà un compte : arrivé par un lien de
  // parrainage, il a dû se connecter au lieu de s'inscrire. Une fois
  // connecté, on demande au serveur de lui appliquer le parrainage ; c'est le
  // serveur qui décide (jamais venu au studio, campagne active, etc.) et qui
  // ne fait rien si l'inscription l'a déjà rattaché. Une seule tentative par
  // montage, et le contexte est vidé dès que le serveur a tranché ; une
  // erreur réseau le laisse en place pour une prochaine visite.
  useEffect(() => {
    if (!isLoggedIn || claimStartedRef.current) return;
    const pending = readPendingReferral();
    if (!pending) return;
    claimStartedRef.current = true;
    HTTP.post<{
      status: 'claimed' | 'not_eligible' | 'retry';
      reason: 'claimed' | 'history' | 'other';
      refereeCredited: boolean;
    }>('/referral/claim', {
      referrerClientId: pending.referrerClientId,
      campaignId: pending.campaignId,
    })
      // Le serveur n'accepte ce POST qu'en JSON (garde anti-formulaire
      // cross-site) ; le helper HTTP n'envoie pas de Content-Type par défaut.
      .setHeader('Content-Type', 'application/json')
      .send()
      .then((res) => {
        // Mindbody injoignable ou claim concurrent : rien n'est tranché, on
        // garde le contexte pour la prochaine visite.
        if (res?.status === 'retry') return;
        clearPendingReferral();
        if (res?.status === 'claimed') {
          queryClient.invalidateQueries({ queryKey: ['/client/services'] });
          queryClient.invalidateQueries({ queryKey: ['/client/purchases'] });
          toast({
            status: 'success',
            title: 'Parrainage appliqué',
            description: res.refereeCredited
              ? 'Votre cadeau de parrainage a été ajouté à votre compte.'
              : 'Votre parrainage est bien enregistré.',
            duration: 8000,
            isClosable: true,
          });
        } else if (res?.reason === 'history') {
          toast({
            status: 'info',
            title: "L'offre de parrainage ne s'applique pas à votre compte",
            description: 'Elle est réservée aux personnes qui découvrent le studio.',
            duration: 8000,
            isClosable: true,
          });
        }
      })
      .catch(() => {
        // Contexte conservé : nouvelle tentative au prochain passage.
      });
  }, [isLoggedIn, queryClient, toast]);

  // Bannière d'erreur lien — affichée que l'utilisateur soit connecté ou non.
  // Volontairement extraite du JSX pour pouvoir la rendre AVANT le early-return
  // logged-in (sinon un admin connecté qui teste un lien cassé ne voit rien).
  const linkErrorAlert =
    referralContext.linkError === 'unsubstituted_template' ? (
      <Alert status="error" variant="left-accent" borderRadius="md" mb={6} alignItems="flex-start">
        <AlertIcon />
        <Box>
          <AlertTitle>Ce lien d'invitation est invalide.</AlertTitle>
          <AlertDescription>
            Le lien que vous avez utilisé n'a pas été personnalisé (le code{' '}
            <code>{'{{id}}'}</code> est resté tel quel). Demandez à votre ami de vous
            renvoyer son lien de parrainage personnel, ou poursuivez votre inscription
            ci-dessous sans parrainage.
          </AlertDescription>
        </Box>
      </Alert>
    ) : null;

  // Bannière statut campagne — affichée quand le filleul arrive sur un lien
  // valide (campaignId + clientId numériques) mais que la campagne n'est pas
  // dans sa fenêtre d'activité (scheduled / expired / exhausted / inactive
  // / not_found). Le hero reste masqué dans ces cas.
  const campaignStatusAlert = (() => {
    if (referralContext.mode !== 'new' || !referralContext.campaignId) return null;
    const status = referralContext.campaignStatus;
    if (status === 'unknown' || status === 'active') return null;

    const startsAt = referralContext.campaign?.startsAt;
    const endsAt = referralContext.campaign?.endsAt;
    const fmt = (iso: string | null | undefined) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    let title = '';
    let body = '';
    switch (status) {
      case 'scheduled': {
        const date = fmt(startsAt);
        title = 'Cette campagne de parrainage n\'a pas encore commencé.';
        body = date
          ? `Elle débute le ${date}. Vous pouvez créer votre compte dès maintenant, mais la récompense de parrainage ne s'appliquera pas.`
          : 'Vous pouvez créer votre compte dès maintenant, mais la récompense de parrainage ne s\'appliquera pas.';
        break;
      }
      case 'expired': {
        const date = fmt(endsAt);
        title = 'Cette campagne de parrainage est terminée.';
        body = date
          ? `Elle s'est terminée le ${date}. Vous pouvez toujours créer votre compte ci-dessous.`
          : 'Vous pouvez toujours créer votre compte ci-dessous.';
        break;
      }
      case 'exhausted': {
        title = 'Cette campagne de parrainage a atteint son quota.';
        body = 'Toutes les places ont été utilisées. Vous pouvez créer votre compte ci-dessous sans bénéficier de la récompense.';
        break;
      }
      case 'inactive': {
        title = 'Cette campagne de parrainage est désactivée.';
        body = 'Vous pouvez créer votre compte ci-dessous sans bénéficier de la récompense.';
        break;
      }
      case 'not_found': {
        title = 'Ce lien de parrainage n\'est pas reconnu.';
        body = 'La campagne associée n\'existe pas (ou plus). Vous pouvez créer votre compte ci-dessous sans parrainage.';
        break;
      }
    }
    return (
      <Alert status="warning" variant="left-accent" borderRadius="md" mb={6} alignItems="flex-start">
        <AlertIcon />
        <Box>
          <AlertTitle>{title}</AlertTitle>
          <AlertDescription>{body}</AlertDescription>
        </Box>
      </Alert>
    );
  })();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      // Error handled
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (isLoggedIn) {
    return (
      <Box className="myAccountContainer">
        {linkErrorAlert}
        {campaignStatusAlert}
        <Stack
          spacing={4}
          width="100%"
          margin="1em 0 2em"
          direction={{ base: 'column-reverse', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ base: 'center', md: 'flex-start' }}
        >
          <Heading
            className="accountMainTitle"
            size="lg"
            textTransform="uppercase"
            textAlign={{ base: 'center', md: 'left' }}
          >
            Mon compte
          </Heading>
          <Button
            className="accountLogoutButton"
            variant="contained"
            alignSelf={{ base: 'flex-end', md: 'center' }}
            onClick={handleLogout}
            isLoading={isLoggingOut}
          >
            Se déconnecter
          </Button>
        </Stack>

        <UserProfile />
      </Box>
    );
  }

  return (
    <Stack w="full" p={{ base: '0.5rem', md: '0' }} className="AccountPages">
      <Heading className="accountMainTitle" size="lg" fontSize="5xl" textTransform="uppercase" mt={6} mb={0}>
        Mon compte
      </Heading>
      <Text align="left" mb={6}>
        Utiliser les mêmes identifiants sur vos comptes{' '}
        <Link href="https://dynamo-cycling.com" isExternal textDecoration="underline" color={primaryColor}>
          dynamo
        </Link>
        {', '}
        <Link href="https://punch-studios.com" isExternal textDecoration="underline" color={primaryColor}>
          Punch
        </Link>{' '}
        {'et, '}
        <Link href="https://riise-studios.com" isExternal textDecoration="underline" color={primaryColor}>
          RIISE
        </Link>{' '}
        : les comptes sont communs.
      </Text>
      {linkErrorAlert}
      {campaignStatusAlert}
      {referralContext.hasReferral &&
        (referralContext.mode === 'legacy' || referralContext.campaignStatus === 'active') && (
          <ReferralWelcome context={referralContext} scrollTargetId={AUTH_SCROLL_TARGET_ID} />
        )}
      <Box id={AUTH_SCROLL_TARGET_ID}>
        <Auth />
      </Box>
    </Stack>
  );
}
